import React, { forwardRef } from 'react'

interface FormFieldProps {
  label: string
  htmlFor?: string
  required?: boolean
  error?: string | null
  children: React.ReactNode
}

/** Shared wrapper: label + control + error message */
export const FormField = ({
  label,
  htmlFor,
  required,
  error,
  children,
}: FormFieldProps) => {
  const errorId = htmlFor ? `${htmlFor}-error` : undefined

  return (
    <div className="employee-form__field">
      <label htmlFor={htmlFor} className="employee-form__label">
        {label}
        {required && <span className="employee-form__required"> *</span>}
      </label>
      {children}
      {error ? (
        <p id={errorId} className="employee-form__field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

interface FormTextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string
  error?: string | null
  success?: boolean
}

/** Text/email input with label, states, and error message */
export const FormTextField = forwardRef<HTMLInputElement, FormTextFieldProps>(
  function FormTextField(
    { label, required, error, success, id, ...inputProps },
    ref,
  ) {
    const errorId = id ? `${id}-error` : undefined

    return (
      <FormField label={label} htmlFor={id} required={required} error={error}>
        <input
          {...inputProps}
          id={id}
          ref={ref}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={`employee-form__input ${
            error ? 'employee-form__input--error' : ''
          } ${success && !error ? 'employee-form__input--success' : ''}`}
        />
      </FormField>
    )
  },
)
